import React from "react";
import { NodeEdge } from "@hex-insights/app-modules";
import {
	addTimeToDate,
	Button,
	CardinalLocation,
	ClassNameProps,
	Column,
	dateTrunc,
	getDateTimeRange,
	getLastDayInMonth,
	Grid,
	If,
	isSameTime,
	makeClassName,
	stringToLocalDate,
	StyleProps,
	Tooltip,
	TruncatedText,
	useClickOutListener,
	useSync,
} from "@hex-insights/core";
import { truncateDisplayItems } from "../../../../Utilities";
import { EventCalendarItem, EventTooltipProps } from "../../types";
import { dateToDateMapKey, getEventColor, rangeDatedItemsToDateMap } from "../../utils";
import { DefaultEventTooltip } from "../Tooltip";
import styles from "./styles.module.css";

export type MonthViewProps = {
	value: Date;
	events?: EventCalendarItem[] | NodeEdge<EventCalendarItem>[];
	onClickDate: (date: Date) => void;
	detailModalVariant?: "default" | "admin";
} & Pick<MonthViewDateEventProps, "EventTooltip"> &
	Partial<ClassNameProps & StyleProps>;

export function MonthView({
	value: monthDate,
	events,
	detailModalVariant = "default",
	onClickDate,
	EventTooltip,
	className,
	style,
}: MonthViewProps) {
	const { rangeStartDate, rangeEndDate } = React.useMemo(() => {
		// TODO locale handling (week start date)
		const monthDateDayJS = addTimeToDate(monthDate);
		const monthEndDate = getLastDayInMonth(monthDate);
		const rangeStartDate = monthDateDayJS.startOf("week").toDate();
		const rangeEndDate = addTimeToDate(dateTrunc(monthEndDate.endOf("week"), "day"), [1, "day"]).toDate();

		return { monthEndDate: monthEndDate.toDate(), rangeStartDate, rangeEndDate };
	}, [monthDate]);

	const dateRange = getDateTimeRange(rangeStartDate, rangeEndDate, "day");

	const dateEventMap = React.useMemo<Record<string, EventCalendarItem[]>>(() => {
		if (!events) {
			return {};
		}
		return rangeDatedItemsToDateMap(
			events,
			(e) => (e.isAllDay ? "startDate" : "startTime"),
			(e) => (e.isAllDay ? "endDate" : "endTime"),
			(e) => e.isAllDay,
		);
	}, [events]);

	return (
		<Column className={makeClassName(styles["month-view"], className)} style={style}>
			<Grid tracks={7}>
				{dateRange.slice(0, 7).map((date, i) => (
					<MonthViewWeekdayLabel key={i} date={date} />
				))}
			</Grid>
			<Grid tracks={7} gap="1px" className={styles["month-view__calendar-grid"]}>
				{dateRange.map((date) => (
					<MonthViewDate
						key={date}
						date={date}
						monthDate={monthDate}
						events={dateEventMap[dateToDateMapKey(date)] ?? []}
						onClick={onClickDate}
						EventTooltip={EventTooltip}
						detailModalVariant={detailModalVariant}
					/>
				))}
			</Grid>
		</Column>
	);
}

type MonthViewWeekdayLabelProps = {
	date: string;
};

function MonthViewWeekdayLabel({ date }: MonthViewWeekdayLabelProps) {
	const dateDate = stringToLocalDate(date, "day");

	return (
		<div className={styles["month-view__weekday-label"]}>
			{dateDate.toLocaleString(undefined, { weekday: "short" })}
		</div>
	);
}

type MonthViewDateProps = {
	date: string;
	monthDate: Date;
	events: EventCalendarItem[];
	onClick: (date: Date) => void;
	detailModalVariant?: "default" | "admin";
} & Pick<MonthViewDateEventProps, "EventTooltip">;

function MonthViewDate({
	date,
	monthDate,
	events,
	onClick,
	detailModalVariant = "default",
	EventTooltip,
}: MonthViewDateProps) {
	const dateDate = stringToLocalDate(date, "day");
	const isToday = isSameTime(dateDate, new Date(), "day");
	const isInMonth = isSameTime(dateDate, monthDate, "month");

	const { displayedItems: displayedEvents, anyHidden, numHidden } = truncateDisplayItems(events, 3);

	const onClickDate = React.useCallback(() => {
		onClick(dateDate);
	}, [dateDate, onClick]);

	return (
		<div
			onDoubleClick={onClickDate}
			className={makeClassName(
				styles["month-view__date"],
				isToday ? styles["month-view__date--today"] : "",
				!isInMonth ? styles["month-view__date--other-month"] : "",
			)}
		>
			<div className={styles["month-view__date__label"]}>{dateDate.getDate()}</div>

			<Column justify="spaced-start" verticalSpacing="0.125rem">
				{displayedEvents.map((event) => (
					<MonthViewDateEvent
						key={event.id}
						calendarEvent={event}
						modalVariant={detailModalVariant}
						EventTooltip={EventTooltip}
					/>
				))}
				<If condition={anyHidden}>
					<Button
						variant="link"
						size="small"
						onClick={onClickDate}
						className={styles["month-view__date__show-more-button"]}
					>
						+ {numHidden} More
					</Button>
				</If>
			</Column>
		</div>
	);
}

const eventTooltipSideOrder: CardinalLocation[] = ["right", "left", "bottom", "top"];

type MonthViewDateEventProps = {
	calendarEvent: EventCalendarItem;
	EventTooltip?: (props: EventTooltipProps) => JSX.Element | null;
	modalVariant?: "default" | "admin";
};

function MonthViewDateEvent({
	calendarEvent,
	EventTooltip = DefaultEventTooltip,
	modalVariant = "default",
}: MonthViewDateEventProps) {
	const eventColor = getEventColor(calendarEvent);

	const { isTooltipOpen, openTooltip, closeTooltip } = Tooltip.useToggle();
	const [isModalOpen, setIsModalOpen] = React.useState(false);
	const ref = React.useRef<HTMLDivElement | null>(null);
	useClickOutListener(ref.current, closeTooltip, isTooltipOpen && !isModalOpen);
	const { syncID, synchronize } = useSync();

	return (
		<div ref={ref} style={{ width: "100%" }}>
			<Tooltip.Container
				isControlled
				isOpen={isTooltipOpen}
				contentSyncID={syncID}
				sideOrder={eventTooltipSideOrder}
				style={{ width: "100%" }}
			>
				<EventTooltip
					item={calendarEvent}
					isOpen={isTooltipOpen}
					synchronizeContent={synchronize}
					withModalBackButton
					setIsModalOpen={setIsModalOpen}
					noStem
					modalVariant={modalVariant}
				/>

				<div
					onClick={openTooltip}
					className={styles["month-view__date__event"]}
					style={{ "--event-color": eventColor } as React.CSSProperties}
				>
					<TruncatedText style={{ position: "relative", zIndex: 1 }}>{calendarEvent.name}</TruncatedText>
				</div>
			</Tooltip.Container>
		</div>
	);
}
